import { useQuery } from '@apollo/client';
import { Redirect, router } from 'expo-router';
import { useAtom, useAtomValue } from 'jotai';
import React, { useEffect } from 'react';
import {
    BodyLargeEmphasized,
    BodyMediumRegular,
    Button,
    ButtonVariant,
    FullScreenContainer,
    H5Regular,
    Spinner,
    View,
    YStack,
} from 'smoothie';

import useGetHolisticSummary from '../hooks/useGetHolisticSummary';
import {
    matchedHolisticAccountsAtom,
    selectedConnectionIdAtom,
} from '../state/HolisticAtoms';
import {
    GET_CONNECTION_QUERY,
    type QuilttConnectionResponse,
} from '../types/Quiltt';
import InstitutionAccounts from './InstitutionAccounts';

const ConnectInstitutionSuccess = () => {
    const connectionId = useAtomValue(selectedConnectionIdAtom);
    if (connectionId == null) {
        return <Redirect href="/" />;
    }

    const { data: holisticData, isLoading: isLoadingHolisticData } =
        useGetHolisticSummary();

    const { data: quilttData, loading: isLoadingQuilttData } =
        useQuery<QuilttConnectionResponse>(GET_CONNECTION_QUERY, {
            variables: { connectionId },
        });

    const [matchedHolisticAccounts, setMatchedHolisticAccounts] = useAtom(
        matchedHolisticAccountsAtom,
    );

    useEffect(() => {
        if (isLoadingHolisticData || isLoadingQuilttData || !holisticData)
            return;

        setMatchedHolisticAccounts(
            Object.values(holisticData)
                .map((category) => category.accounts)
                .flat()
                .filter((account) => account.connectionId === connectionId),
        );
    }, [holisticData, quilttData]);

    const { institution } = quilttData?.connection ?? {};

    return (
        <FullScreenContainer
            header={
                <View
                    paddingVertical={8}
                    paddingHorizontal={16}
                    flex={1}
                    alignItems="center"
                    justifyContent="center"
                >
                    <BodyLargeEmphasized>
                        {institution?.name} accounts
                    </BodyLargeEmphasized>
                </View>
            }
            footer={
                <Button
                    variant={ButtonVariant.PRIMARY}
                    text="Save"
                    onPress={() => {
                        router.navigate('/');
                    }}
                />
            }
        >
            <YStack gap={24} paddingTop={16}>
                <YStack gap={8}>
                    <H5Regular>Connected accounts</H5Regular>
                    <BodyMediumRegular color="$gray1000">
                        Review the connected accounts below and add any
                        additional or missing details.
                    </BodyMediumRegular>
                </YStack>
                {isLoadingHolisticData || isLoadingQuilttData ? (
                    <Spinner />
                ) : (
                    <InstitutionAccounts
                        institutionName={institution?.name ?? ''}
                        institutionLogo={institution?.logo?.url ?? ''}
                        connectionId={connectionId}
                        accounts={matchedHolisticAccounts}
                        context="onboarding"
                    />
                )}
            </YStack>
        </FullScreenContainer>
    );
};

export default ConnectInstitutionSuccess;
